import { appInsights } from '../services/appInsights';
import { logAudit } from './auditUtil';

type TrackApiRequestAuditArgs = {
  auth: any;
  payload: any;
  action: string;
  auditType: string;
  requestMethod?: string;
  traceMessage?: string;
};

const getUsername = (auth: any) => `${auth?.user?.result?.first_name ?? ''} ${auth?.user?.result?.last_name ?? ''}`.trim();
const MAX_AUDIT_VALUE_LENGTH = 1000;
const MAX_AUDIT_TYPE_LENGTH = 25;
const MAX_AUDIT_ACTION_LENGTH = 30;

const serializePayload = (payload: any) => {
  try {
    return JSON.stringify(payload);
  } catch (error) {
    return `[payload-serialization-failed: ${String(error)}]`;
  }
};

export const formatAction = (action?: string) => {
  const rawAction = `${action}`;

  if (rawAction.length <= MAX_AUDIT_ACTION_LENGTH) return rawAction;

  return `${rawAction.slice(0, MAX_AUDIT_ACTION_LENGTH)}`;
};

export const formatType = (auditType: any, requestMethod?: string) => {
  const rawType = `${auditType}, method: ${requestMethod ?? 'unknown'}`;

  if (rawType.length <= MAX_AUDIT_TYPE_LENGTH) return rawType;

  return `${rawType.slice(0, MAX_AUDIT_TYPE_LENGTH)}`;
};

const formatAuditValue = (payload: any) => {
  const rawValue = `Payload: ${serializePayload(payload)}`;
  if (rawValue.length <= MAX_AUDIT_VALUE_LENGTH) return rawValue;

  const suffix = `...[truncated ${rawValue.length - MAX_AUDIT_VALUE_LENGTH} chars]`;
  return `${rawValue.slice(0, MAX_AUDIT_VALUE_LENGTH - suffix.length)}${suffix}`;
};

export const trackApiRequestAudit = async ({ auth, payload, action, auditType, requestMethod, traceMessage }: TrackApiRequestAuditArgs) => {
  const email = auth?.user?.result?.email ?? 'unknown-user';
  appInsights.trackTrace({ message: traceMessage ?? `${auditType} submitted by ${email}` });

  const auditObj = {
    audit_ts: new Date(),
    user_id: auth?.user?.result?.id,
    username: getUsername(auth),
    audit_type: formatType(auditType, requestMethod),
    action: formatAction(action),
    value: formatAuditValue(payload),
  };

  await logAudit(auth, auditObj);
};

type AwardPaymentAuditAction = 'save' | 'submit';
type TrackAwardPaymentRequestAuditArgs = Omit<TrackApiRequestAuditArgs, 'auditType'> & { action: AwardPaymentAuditAction };

export const trackAwardPaymentRequestAudit = async ({ auth, payload, action, requestMethod }: TrackAwardPaymentRequestAuditArgs) =>
  trackApiRequestAudit({
    auth,
    payload,
    action,
    requestMethod,
    auditType: 'Award Payment Request',
  });
